import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import type { IProduct } from '../../@types/product';
import { AppDispatch, type RootState } from '../../redux/store';
import { getProducts, resetCart } from '../../redux/slices/product';
import axios from '../../utils/axios';

// New components
import VoiceOrderingLayout from './components/VoiceOrderingLayout';
import VoiceStatusDisplay from './components/VoiceStatusDisplay';
import TrioSelectionModal from './components/TrioSelectionModal';
import RepeatOrderModal from './components/RepeatOrderModal';
import ArrivalVideo from './components/ArrivalVideo';
import { useVoiceOrderingEnhanced } from './hooks/useVoiceOrderingEnhanced';
import { useGeminiVoiceOrdering } from './hooks/useGeminiVoiceOrdering';
import { useAuthContext } from '../../auth/useAuthContext';
import { getOrganization } from '../../redux/slices/organization';
import { useSettingsContext } from '../../components/settings';

export default function GeneralBookingPage() {
  const dispatch = useDispatch<AppDispatch>();
  const products = useSelector((state: RootState) => state.product?.products ?? []);
  const cart = useSelector((state: RootState) => state.product?.checkout?.cart ?? []);
  const organization = useSelector((state: RootState) => state.organization?.organization);
  const { user } = useAuthContext();
  const {
    themeGemini: useGemini,
    themeMinimal: useMinimalDesign,
    onToggleGemini,
    onToggleMinimal,
  } = useSettingsContext();

  const [tableData, setTableData] = useState<IProduct[]>([]);
  const [trioOptionsMap, setTrioOptionsMap] = useState<Map<string, any>>(new Map());
  const [showArrivalVideo, setShowArrivalVideo] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<'FR' | 'EN' | null>(null);
  const prevGeminiRef = useRef<boolean | null>(null);

  // Play arrival video on initial load if Gemini is enabled,
  // and on every toggle from OFF to ON
  useEffect(() => {
    if (useGemini && (prevGeminiRef.current === null || prevGeminiRef.current === false)) {
      setShowArrivalVideo(true);
    }
    prevGeminiRef.current = useGemini;
  }, [useGemini]);

  // Load all products, trio options, and current organization on mount
  useEffect(() => {
    dispatch(getProducts());
    if (user?.subOrgId) {
      dispatch(getOrganization(user.subOrgId));
      // ============================================================
      // 🚀 OPTIMISATION : Préchargement batch des options trio
      // AVANT : chaque produit faisait GET /get_trio_options (Python)
      //         → ~300ms par item de perdu
      // MAINTENANT : un seul appel GET /api/v1/products/trio-options/
      //   organization/{orgId} (Spring Boot) → Map<productId, options>
      //   Plus aucun appel réseau dans le flux Gemini !
      // Fallback : si le endpoint Spring Boot n'est pas dispo,
      //   les produits absents de la Map sont ignorés (trioPrice = 0)
      // ============================================================
      axios.get(`/api/v1/products/trio-options/organization/${user.subOrgId}`)
        .then(res => {
          const map = new Map<string, any>();
          (res.data || []).forEach((item: any) => {
            map.set(String(item.productId), {
              product: item.product,
              option_groups: item.option_groups,
            });
          });
          setTrioOptionsMap(map);
        })
        .catch(err => {
          console.warn('⚠️ Trio options batch endpoint not available yet:', err.message);
        });
    } else if (user?.organization?.id) {
      // Support for old format just in case
      dispatch(getOrganization(user.organization.id));
    }
  }, [dispatch, user]);

  // Update local tableData when products change
  useEffect(() => {
    if (products.length > 0) {
      setTableData(products);
    }
  }, [products]);

  // ============================================================
  // 🔀 SÉLECTION DU FLUX : Gemini vs Legacy
  //   useGemini = true  → useGeminiVoiceOrdering (Flash Live API)
  //     → STT/TTS/LLM natifs dans une WebSocket temps réel
  //     → 10 outils, voix Aoede, pas d'appel Python
  //   useGemini = false → useVoiceOrderingEnhanced (Legacy)
  //     → Web Speech API (STT) → GPT-4o-mini (NLU) → POST /extract_order
  //     → 80 keywords frontend, Python /get_trio_options
  // Les deux hooks sont TOUJOURS appelés (règle React),
  // mais seul celui correspondant au switch est activé
  // ============================================================
  const voiceOrderingLegacy = useVoiceOrderingEnhanced({ products, cart, isEnabled: !useGemini });
  const voiceOrderingGemini = useGeminiVoiceOrdering({ products, cart, isEnabled: useGemini && !showArrivalVideo, trioOptionsMap });
  
  // Decide which to use based on the switch state
  const voiceOrdering = useGemini ? voiceOrderingGemini : (voiceOrderingLegacy as any);

  // Sync detected language from Gemini hook
  useEffect(() => {
    if (useGemini && voiceOrderingGemini.detectedLanguage) {
      setDetectedLanguage(voiceOrderingGemini.detectedLanguage);
    }
  }, [useGemini, voiceOrderingGemini.detectedLanguage]);

  // Reset detected language when Gemini is toggled off
  useEffect(() => {
    if (!useGemini) {
      setDetectedLanguage(null);
    }
  }, [useGemini]);

  // Create the voice status component
  const voiceStatusComponent = (
    <VoiceStatusDisplay
      isOrderActive={voiceOrdering.isOrderActive}
      isProcessingOrder={voiceOrdering.isProcessingOrder}
      isConfirmingOrder={voiceOrdering.isConfirmingOrder}
      phase={voiceOrdering.phase}
      trioSelection={voiceOrdering.trioSelection}
      currentTranscript={voiceOrdering.currentTranscript}
      accumulatedTranscript={voiceOrdering.accumulatedTranscript}
      loadingDots={voiceOrdering.loadingDots}
      instructions={voiceOrdering.instructions}
      agentName={voiceOrdering.agentName}
      agentIsSpeaking={voiceOrdering.isSpeaking}
    />
  );

  return (
    <>
      <Helmet>
        <title>Born UI</title>
      </Helmet>

      <VoiceOrderingLayout
        products={tableData}
        extractedData={voiceOrdering.extractedData}
        trioData={voiceOrdering.trioItems}
        onRemoveSolo={voiceOrdering.removeSoloItem}
        onRemoveTrio={voiceOrdering.removeTrioItem}
        voiceStatusComponent={voiceStatusComponent}
        isOrderActive={voiceOrdering.isOrderActive}
        isProcessingOrder={voiceOrdering.isProcessingOrder}
        isConfirmingOrder={voiceOrdering.isConfirmingOrder}
        isMinimal={useMinimalDesign}
        onToggleMinimal={onToggleMinimal}
        onResetOrder={() => {
          dispatch(resetCart());
        }}
        onCheckout={() => {
          alert("Commande validée !");
          dispatch(resetCart());
        }}
        useGemini={useGemini}
        onToggleGemini={(enabled) => {
          if (enabled) {
            setShowArrivalVideo(true);
            onToggleGemini();
          } else {
            setShowArrivalVideo(false);
            onToggleGemini();
            voiceOrderingGemini.manualStop();
          }
        }}
        detectedLanguage={organization?.autoDetectLanguage ? detectedLanguage : null}
        title="| La listes des produits |"
        showRecommendations
        showPromotionalImage
      />

      {/* Arrival Video */}
      <ArrivalVideo
        open={showArrivalVideo}
        onComplete={() => {
          setShowArrivalVideo(false);
          voiceOrderingGemini.manualStart();
        }}
      />

      {/* Repeat Order Modal */}
      {useGemini && (
        <RepeatOrderModal
          lastOrder={voiceOrderingGemini.lastOrderData}
          isOpen={voiceOrderingGemini.showRepeatModal}
          onAccept={voiceOrderingGemini.acceptLastOrder}
          onDecline={voiceOrderingGemini.declineLastOrder}
        />
      )}

      {/* Trio Selection Modal */}
      <TrioSelectionModal
        trioSelection={voiceOrdering.trioSelection}
        isOpen={voiceOrdering.phase === 'trio_selection'}
        currentTranscript={voiceOrdering.currentTranscript}
      />
    </>
  );
}